import React, { useState } from 'react';
import { ChevronRight, File, Folder, Download, Loader } from 'lucide-react';
import { FileMetadata } from '../services/firestoreService';
import { Button } from './ui/button';

interface FileTreeNodeProps {
  year: number;
  files: FileMetadata[];
  onDownload: (file: FileMetadata) => Promise<void>;
  isLoading?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const FileTreeNode: React.FC<FileTreeNodeProps> = ({
  year,
  files,
  onDownload,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const handleDownload = async (file: FileMetadata) => {
    setDownloadingFileId(file.id);
    try {
      await onDownload(file);
    } finally {
      setDownloadingFileId(null);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full p-2 hover:bg-gray-100 rounded transition-colors"
      >
        <ChevronRight
          size={18}
          className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
        <Folder size={18} className="text-amber-500" />
        <span className="font-semibold text-gray-700">{year}</span>
        <span className="text-xs text-gray-500 ml-auto">({files.length} file{files.length !== 1 ? 's' : ''})</span>
      </button>

      {isExpanded && (
        <div className="ml-6 space-y-1 border-l border-gray-200 pl-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <File size={16} className="text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {file.fileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.fileSize)}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(file)}
                disabled={downloadingFileId === file.id || isLoading}
                className="ml-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                {downloadingFileId === file.id ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileTreeNode;
